import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { cloneDeep } from 'lodash';

import { environment } from 'src/environments/environment';

import { ComponentsService } from '../../services/components.service';
import { LayoutEditorBase } from '../layout-editor-base/layout-editor.base';
import { AttributeDataService } from '../../services/attribute-data.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { ComponentUtilsService } from '../services/component-utils.service';
import { RssFeedValidationService } from '../services/rss-feed-validation.service';

@Component({
  selector: 'template-component-layout-rss',
  templateUrl: './layout-rss.component.html',
  styleUrls: ['./layout-rss.component.scss'],
  standalone: false,
})
export class LayoutRssComponent extends LayoutEditorBase {
  protected readonly layoutComponent = 'rise-layout-rss';

  readonly LayoutType = ComponentsService.LAYOUTS_MAP['rise-layout-rss'];
  private static readonly DEFAULT_LAYOUT_ID =
    ComponentsService.LAYOUTS_MAP['rise-layout-rss'].CARDS;

  private static readonly DEFAULT_SLIDE_DURATION = 6;
  private static readonly DEFAULT_TEXT_COLOR = 'rgba(17, 24, 39, 1)';
  private static readonly DEFAULT_ACCENT_COLOR = 'rgba(0, 135, 90, 1)';
  private static readonly DEFAULT_BACKGROUND_COLOR = 'rgba(255, 255, 255, 0.8)';

  /**
   * Order matches left-to-right DOM order of the rendered radio buttons.
   */
  private readonly layoutOrder = [
    this.LayoutType.CARDS,
    this.LayoutType.SINGLE,
    this.LayoutType.HEADLINE,
  ];

  @ViewChildren('layoutOptionBtn')
  layoutOptionBtns: QueryList<ElementRef<HTMLButtonElement>>;

  public spinner = false;
  public feedUrl = '';
  public validationResult: string;
  public itemCount = '10';
  public slideDuration = LayoutRssComponent.DEFAULT_SLIDE_DURATION;
  public scrollSpeed = 'medium';
  public showImages = true;
  public showCategories = true;
  public showDescription = true;
  public showPublishDate = true;
  public textColor = LayoutRssComponent.DEFAULT_TEXT_COLOR;
  public accentColor = LayoutRssComponent.DEFAULT_ACCENT_COLOR;
  public backgroundColor = LayoutRssComponent.DEFAULT_BACKGROUND_COLOR;

  public componentServices: Record<string, any>[];
  private _pendingValidationCount = 0;

  /** URL/parse failures — red input border and exclamation icon. */
  get hasUrlError(): boolean {
    return (
      !!this.validationResult &&
      this.validationResult !== 'VALID' &&
      this.validationResult !== 'INVALID_FEED'
    );
  }

  /** Valid URL (including W3C feed warnings) — green checkmark. */
  get hasValidUrl(): boolean {
    return (
      !!this.feedUrl &&
      (this.validationResult === 'VALID' ||
        this.validationResult === 'INVALID_FEED')
    );
  }

  /** W3C feed validator reported errors while the URL itself is valid. */
  get hasW3cValidationErrors(): boolean {
    return this.validationResult === 'INVALID_FEED';
  }

  /** Direct link to the W3C feed validator for the current feed URL. */
  get w3cValidationUrl(): string {
    const feed = !this.feedUrl ? '' : this.feedUrl.trim();
    return `${RssFeedValidationService.VALIDATOR_URL}${encodeURIComponent(feed)}`;
  }

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private attributeDataService: AttributeDataService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private componentUtils: ComponentUtilsService,
    private rssFeedValidation: RssFeedValidationService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super();

    this.componentsService.registerDirective({
      type: 'rise-layout-rss',
      element: this.elementRef.nativeElement,
      show: (options?) => {
        if (this.componentsService.selected?.id) {
          this.componentId = this.componentsService.selected.id;
          this.load();
        }
      },
    });
  }

  load() {
    const defaultAttributes =
      this.componentsService.getSelectedDirective()?.defaultAttributes || {};
    const defaultLayout = cloneDeep(defaultAttributes?.layout || {});
    const defaultServices = cloneDeep(defaultAttributes?.services || []);

    const rawLayout =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'layout',
      ) || defaultLayout;
    const rawServices =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'services',
      ) || defaultServices;

    this.componentLayout = this.templateEditorUtils.safeParseJson(
      rawLayout,
      defaultLayout,
    );
    this.componentServices = this.templateEditorUtils.safeParseJson(
      rawServices,
      defaultServices,
    );

    this.loadLayoutSelection(LayoutRssComponent.DEFAULT_LAYOUT_ID);
    // A previously-stored layout may reference an id that no longer exists
    // (e.g. a removed layout) — fall back so the radiogroup always has a
    // selected, focusable button and the actual widget doesn't try to load
    // an unknown layout.
    const layoutIsAvailable =
      this.isCustomLayout || this.layoutOrder.includes(this.layout);
    if (!layoutIsAvailable) {
      this.layout = LayoutRssComponent.DEFAULT_LAYOUT_ID;
    }

    const layoutAttributes = this.componentLayout?.attributes || {};
    this.slideDuration = this._normalizeSlideDurationSeconds(
      layoutAttributes.slideDuration,
    );
    this.scrollSpeed = (layoutAttributes.scrollSpeed as string) || 'medium';
    this.showImages =
      layoutAttributes.showImages !== undefined
        ? !!layoutAttributes.showImages
        : true;
    this.showCategories =
      layoutAttributes.showCategories !== undefined
        ? !!layoutAttributes.showCategories
        : true;
    this.showDescription =
      layoutAttributes.showDescription !== undefined
        ? !!layoutAttributes.showDescription
        : true;
    this.showPublishDate =
      layoutAttributes.showPublishDate !== undefined
        ? !!layoutAttributes.showPublishDate
        : true;
    this.textColor =
      (layoutAttributes.textColor as string) ??
      LayoutRssComponent.DEFAULT_TEXT_COLOR;
    this.accentColor =
      (layoutAttributes.accentColor as string) ??
      LayoutRssComponent.DEFAULT_ACCENT_COLOR;
    this.backgroundColor =
      (layoutAttributes.backgroundColor as string) ??
      LayoutRssComponent.DEFAULT_BACKGROUND_COLOR;

    const serviceAttributes =
      Array.isArray(this.componentServices) && this.componentServices.length > 0
        ? this.componentServices[0]?.attributes || {}
        : {};
    this.feedUrl = serviceAttributes.feedurl || '';
    this.itemCount = serviceAttributes.maxitems
      ? serviceAttributes.maxitems.toString()
      : '10';

    // if environment is not defined, this is a new component — persist defaults
    const env = this.attributeDataService.getAttributeData(
      this.componentId,
      'environment',
    );
    if (!env || !layoutIsAvailable) {
      this.save();
    }

    this.saveFeed();
  }

  saveFeed() {
    if (this._validateFeedUrl()) {
      if (!this.feedUrl?.trim()) {
        this._saveFeedUrl();
      } else {
        void this._isFeedParsable();
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  _validateFeedUrl(): boolean {
    this.validationResult = '';

    const feed = !this.feedUrl ? '' : this.feedUrl.trim();
    if (feed === '') {
      return true;
    }

    if (this.componentUtils.isValidUrl(feed)) {
      return true;
    }

    this.validationResult = 'INVALID_URL';
    return false;
  }

  async _isFeedParsable(): Promise<void> {
    const validatedUrl = this.feedUrl;
    this._pendingValidationCount++;
    this.spinner = true;
    try {
      const result = (await this.rssFeedValidation.isParsable(
        validatedUrl,
      )) as string;
      if (this.feedUrl !== validatedUrl) return;
      if (result === 'VALID') {
        this._saveFeedUrl();
        await this._isFeedValid();
      } else {
        this.validationResult = result;
      }
    } finally {
      this._pendingValidationCount--;
      if (this._pendingValidationCount === 0) {
        this.spinner = false;
      }
      this.changeDetectorRef.detectChanges();
    }
  }

  async _isFeedValid(): Promise<void> {
    this.validationResult = (await this.rssFeedValidation.isValid(
      this.feedUrl,
    )) as string;
  }

  _saveFeedUrl() {
    if (
      !Array.isArray(this.componentServices) ||
      !this.componentServices.length
    ) {
      this.componentServices = [{}];
    }
    if (!this.componentServices[0].attributes) {
      this.componentServices[0].attributes = {};
    }
    this.componentServices[0].attributes.feedurl = this.feedUrl;

    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
  }

  setLayout(layoutId: string) {
    this.layout = layoutId;
    if (layoutId !== this.LayoutType.HEADLINE) {
      this.scrollSpeed = 'medium';
    }
    this.save();
  }

  /**
   * Handles keyboard navigation within the layout radiogroup.
   * Implements the ARIA radiogroup pattern: arrow keys move focus and
   * selection, only the selected option is in the tab sequence.
   */
  onLayoutKeydown(event: KeyboardEvent, layoutId: string): void {
    const idx = this.layoutOrder.indexOf(layoutId);
    let nextIdx: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIdx = (idx + 1) % this.layoutOrder.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIdx = (idx - 1 + this.layoutOrder.length) % this.layoutOrder.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = this.layoutOrder.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.setLayout(this.layoutOrder[nextIdx]);
    this.layoutOptionBtns.get(nextIdx)?.nativeElement.focus();
  }

  save() {
    this.applyLayoutSelection();

    this.slideDuration = this._normalizeSlideDurationSeconds(
      this.slideDuration,
    );

    this.componentLayout.attributes = {
      slideDuration: this.slideDuration,
      scrollSpeed: this.scrollSpeed,
      showImages: this.showImages,
      showCategories: this.showCategories,
      showDescription: this.showDescription,
      showPublishDate: this.showPublishDate,
      textColor: this.textColor,
      accentColor: this.accentColor,
      backgroundColor: this.backgroundColor,
    };

    if (
      !Array.isArray(this.componentServices) ||
      !this.componentServices.length
    ) {
      this.componentServices = [{}];
    }
    if (!this.componentServices[0].attributes) {
      this.componentServices[0].attributes = {};
    }
    const parsedItemCount = parseInt(this.itemCount, 10);
    this.componentServices[0].attributes = {
      ...this.componentServices[0].attributes,
      maxitems: Number.isNaN(parsedItemCount) ? 10 : parsedItemCount,
    };

    this.attributeDataService.setAttributeData(
      this.componentId,
      'layout',
      this.componentLayout,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'services',
      this.componentServices,
    );
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }

  /**
   * Coerces to a whole number of seconds and enforces a minimum of 1 (invalid / non-positive → at least 1).
   */
  private _normalizeSlideDurationSeconds(raw: unknown): number {
    const coerced =
      this.templateEditorUtils.intValueFor(
        raw,
        LayoutRssComponent.DEFAULT_SLIDE_DURATION,
      ) ?? LayoutRssComponent.DEFAULT_SLIDE_DURATION;
    return Math.max(1, coerced);
  }
}
